import i18n from "@/i18n.ts";
import {
  ApiWorkflowEventTypes,
  isCandidateWorkflowEventType,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { TFunction } from "i18next";

export enum EmailPlaceholder {
  CANDIDATE_LAST = "{{candidate.last_name}}",
  CANDIDATE_FIRST = "{{candidate.first_name}}",
  CANDIDATE_FULL = "{{candidate.full_name}}",
  CANDIDATE_EMAIL = "{{candidate.email}}",
  JOB_TITLE = "{{job.title}}",
  COMPANY_NAME = "{{company.name}}",
  SENDER_FIRST = "{{sender.first_name}}",
  SENDER_LAST = "{{sender.last_name}}",
  SENDER_FULL = "{{sender.full_name}}",
  SENDER_EMAIL = "{{sender.email}}",
  SENDER_JOB_TITLE = "{{sender.job_title}}",
  SENDER_PHONE = "{{sender.phone}}",
}

export interface PlaceholderOption {
  id: string;
  title: string;
  group?: string;
  subgroup?: string;
}

export const getEmailPlaceholderOptions = (t: TFunction): PlaceholderOption[] => [
  {
    id: EmailPlaceholder.CANDIDATE_LAST,
    title: t("core:email_placeholder_candidate_last_name"),
  },
  {
    id: EmailPlaceholder.CANDIDATE_FIRST,
    title: t("core:email_placeholder_candidate_first_name"),
  },
  {
    id: EmailPlaceholder.CANDIDATE_FULL,
    title: t("core:email_placeholder_candidate_full_name"),
  },
  {
    id: EmailPlaceholder.CANDIDATE_EMAIL,
    title: t("core:email_placeholder_candidate_email"),
  },
  {
    id: EmailPlaceholder.JOB_TITLE,
    title: t("ats:job"),
  },
  {
    id: EmailPlaceholder.COMPANY_NAME,
    title: t("common:company"),
  },
  {
    id: EmailPlaceholder.SENDER_FIRST,
    title: t("core:email_placeholder_sender_first_name"),
  },
  {
    id: EmailPlaceholder.SENDER_LAST,
    title: t("core:email_placeholder_sender_last_name"),
  },
  {
    id: EmailPlaceholder.SENDER_FULL,
    title: t("core:email_placeholder_sender_full_name"),
  },
  {
    id: EmailPlaceholder.SENDER_EMAIL,
    title: t("core:email_placeholder_sender_email"),
  },
  {
    id: EmailPlaceholder.SENDER_JOB_TITLE,
    title: t("core:email_placeholder_sender_job_title"),
  },
  {
    id: EmailPlaceholder.SENDER_PHONE,
    title: t("core:email_placeholder_sender_phone"),
  },
];

export const getGDPRPlaceholders = (t: TFunction): PlaceholderOption[] => {
  return [
    {
      title: t("common:duration"),
      id: "{{data_protection.duration}}",
    },
    {
      title: t("data_protection:privacy_policy_link"),
      id: "{{data_protection.privacy_policy_link}}",
    },
    {
      title: t("data_protection:request_to_edit"),
      id: "{{data_protection.request_to_edit}}",
    },
    {
      title: t("data_protection:request_to_delete"),
      id: "{{data_protection.request_to_delete}}",
    },
    {
      title: t("common:company"),
      id: "{{company.name}}",
    },
  ];
};

const getWorkflowEmployeeOptions = (): PlaceholderOption[] => [
  {
    id: "{{employee.id}}",
    title: i18n.t("core:employee_id"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.first_name}}",
    title: i18n.t("core:first_name"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.last_name}}",
    title: i18n.t("core:last_name"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.full_name}}",
    title: i18n.t("core:full_name"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.email}}",
    title: i18n.t("core:email"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.email_personal}}",
    title: i18n.t("core:email_personal"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.mention}}",
    title: "@" + i18n.t("core:mention").toLowerCase(),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.start_date}}",
    title: i18n.t("core:hired_on"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.termination_date}}",
    title: i18n.t("core:termination_date"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.probation_ends_date}}",
    title: i18n.t("workflow:trigger_schedule_entity_field_probation_ends_date"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.personnel_number}}",
    title: i18n.t("core:employee_number"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.manager_full_name}}",
    title: i18n.t("core:manager"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.department_name}}",
    title: i18n.t("core:department"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.position_name}}",
    title: i18n.t("core:position"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.anniversary_short_date}}",
    title: i18n.t("workflow:variable_anniversary_short_date"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.anniversary_years}}",
    title: i18n.t("workflow:variable_anniversary_years"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.birthday_date}}",
    title: i18n.t("workflow:variable_birthday_date"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.birthday_short_date}}",
    title: i18n.t("workflow:variable_birthday_short_date"),
    group: i18n.t("core:employee"),
  },
  {
    id: "{{employee.birthday_years}}",
    title: i18n.t("workflow:variable_birthday_years"),
    group: i18n.t("core:employee"),
  },
];

export const getWorkflowAssigneeOptions = (): PlaceholderOption[] => [
  {
    id: "{{assignee.id}}",
    title: i18n.t("core:employee_id"),
    group: i18n.t("workflow:variable_group_assignee"),
  },
  {
    id: "{{assignee.first_name}}",
    title: i18n.t("core:first_name"),
    group: i18n.t("workflow:variable_group_assignee"),
  },
  {
    id: "{{assignee.last_name}}",
    title: i18n.t("core:last_name"),
    group: i18n.t("workflow:variable_group_assignee"),
  },
  {
    id: "{{assignee.full_name}}",
    title: i18n.t("core:full_name"),
    group: i18n.t("workflow:variable_group_assignee"),
  },
  {
    id: "{{assignee.email}}",
    title: i18n.t("core:email"),
    group: i18n.t("workflow:variable_group_assignee"),
  },
];

const getWorkflowTimeOffOptions = (): PlaceholderOption[] => [
  {
    id: "{{time_off.category_name}}",
    title: i18n.t("time_off:category"),
    group: i18n.t("time_off:time_off"),
  },
  {
    id: "{{time_off.category_emoji}}",
    title: i18n.t("common:emoji"),
    group: i18n.t("time_off:time_off"),
  },
  {
    id: "{{time_off.date_from}}",
    title: i18n.t("common:date_from"),
    group: i18n.t("time_off:time_off"),
  },
  {
    id: "{{time_off.date_to}}",
    title: i18n.t("common:date_to"),
    group: i18n.t("time_off:time_off"),
  },
  {
    id: "{{time_off.balance_amount}}",
    title: i18n.t("time_off:balance"),
    group: i18n.t("time_off:time_off"),
  },
];

const getWorkflowGoogleWorkspaceOptions = (): PlaceholderOption[] => [
  {
    id: "{{google_workspace.email}}",
    title: i18n.t("core:email"),
    group: "Google Workspace",
  },
  {
    id: "{{google_workspace.password}}",
    title: i18n.t("common:password"),
    group: "Google Workspace",
  },
];

const getWorkflowCandidateOptions = (): PlaceholderOption[] => [
  {
    id: "{{candidate.first_name}}",
    title: i18n.t("core:first_name"),
    group: i18n.t("ats:candidate"),
  },
  {
    id: "{{candidate.last_name}}",
    title: i18n.t("core:last_name"),
    group: i18n.t("ats:candidate"),
  },
  {
    id: "{{candidate.full_name}}",
    title: i18n.t("core:full_name"),
    group: i18n.t("ats:candidate"),
  },
  {
    id: "{{candidate.email}}",
    title: i18n.t("core:email"),
    group: i18n.t("ats:candidate"),
  },
  {
    id: "{{candidate.stage_name}}",
    title: i18n.t("ats:stage"),
    group: i18n.t("ats:candidate"),
  },
  {
    id: "{{candidate.link}}",
    title: i18n.t("common:link"),
    group: i18n.t("ats:candidate"),
  },
];

const getWorkflowJobOptions = (): PlaceholderOption[] => [
  {
    id: "{{job.title}}",
    title: i18n.t("common:title"),
    group: i18n.t("ats:job"),
  },
];

const getWorkflowCompanyOptions = (): PlaceholderOption[] => [
  {
    id: "{{company.name}}",
    title: i18n.t("common:title"),
    group: i18n.t("common:company"),
  },
];

export const getWorkflowPlaceholderOptions = (
  eventType?: ApiWorkflowEventTypes | null
): PlaceholderOption[] =>
  isCandidateWorkflowEventType(eventType)
    ? [...getWorkflowCandidateOptions(), ...getWorkflowJobOptions(), ...getWorkflowCompanyOptions()]
    : [
        ...getWorkflowEmployeeOptions(),
        ...getWorkflowTimeOffOptions(),
        ...getWorkflowGoogleWorkspaceOptions(),
      ];

export const getWorkflowTaskPlaceholderOptions = (): PlaceholderOption[] => [
  ...getWorkflowAssigneeOptions(),
  ...getWorkflowCandidateOptions(),
  ...getWorkflowJobOptions(),
  ...getWorkflowCompanyOptions(),
];

export const getReminderPlaceholderOptions = (): PlaceholderOption[] => [
  ...getWorkflowEmployeeOptions(),
];

export const getPerformanceReviewInvitationPlaceholderOptions = (): PlaceholderOption[] => [
  {
    id: "{{participant.full_name}}",
    title: i18n.t("performance_review:variable_participant_full_name"),
  },
];

export type RichEditorBodyFileType = "video" | "pdf";
